import { NgModule } from '@angular/core';
import {
  ApolloModule,
  APOLLO_OPTIONS,
  NamedOptions,
  APOLLO_NAMED_OPTIONS
} from 'apollo-angular';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { environment } from '../environments/environment';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { SpinnerService } from './core-module/services';

const uri = environment.graphQlUri; // <-- add the URL of the GraphQL server here

export function createApollo(httpLink: HttpLink) {
  const basic = setContext((operation, context) => ({
    headers: {}
  }));

  /*
  IF GraphQL Operation is 'GetSession' then add X-Skip-Session-Check to bypass
  adding token to request.

  'GetSession' is what's called to pass token to backend to see if token is still viable
  and returns user profile associated with token.

  if no token is available, then return an empty user profile

  if token has expired, then throw error

  'X-Skip-Session-Check' is used in session.interceptor
  */

  const skipSessionCheckLink = setContext((operation, context) => {
    if (operation.operationName === 'GetSession' || operation.operationName === 'RefreshSession') {
      return {
        headers: {
          ...context.headers,
          'X-Skip-Session-Check': 'true'
        }
      };
    }

    /*
     * AUTH.9. An old goals report link is opened during start-up and needs no
     * session, so it is not queued behind one. Both of its calls render their
     * own outcome (the expired card, the banner), so a refusal must reach them
     * instead of replacing the page with an error screen.
     */
    if (operation.operationName === 'OpenReturnLink') {
      return {
        headers: {
          ...context.headers,
          'X-Skip-Session-Check': 'true',
          'X-Skip-Error-Redirect': 'true'
        }
      };
    }

    if (operation.operationName === 'ClaimReturnLink') {
      return {
        headers: {
          ...context.headers,
          'X-Skip-Error-Redirect': 'true'
        }
      };
    }

    return {
      headers: {
        ...context.headers
      }
    };
  });

  const http = httpLink.create({ uri });
  const middleware = new ApolloLink((operation, forward) => {
    return forward(operation);
  });
  const error = onError(
    ({ networkError, graphQLErrors, operation, response }) => {
      if (graphQLErrors?.length > 0) {
        const code = graphQLErrors[0].extensions.code;
        const message = graphQLErrors[0].message;
        console.error(`${code}: ${message}`);
      }
    }
  );

  const link = ApolloLink.from([
    basic,
    skipSessionCheckLink,
    middleware,
    error,
    http
  ]);
  const cache = new InMemoryCache({
    resultCaching: false
  });

  return {
    link,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore'
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      }
    }
  };
}

function apolloNameFactory(httpLink: HttpLink): NamedOptions {
  return {
    datastopInstitution: {
      cache: new InMemoryCache(),
      link: ApolloLink.from([
        httpLink.create({
          uri: environment.datastopInstitutionGraphqlUri
        })
      ])
    }
  };
}

@NgModule({
  exports: [ApolloModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, SpinnerService]
    },
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory: apolloNameFactory,
      deps: [HttpLink]
    }
  ]
})
export class GraphQLModule {}
